/**
 * The contract between this package and Dart.
 *
 * This file is the ONE place the surface is declared. Dart binds against it
 * through `apps/web/lib/features/call/interop/media_web.dart`, and a golden
 * test compares the two so a rename here cannot silently break the app at
 * runtime -- Dart cannot type-check TypeScript, so drift is otherwise only
 * discovered in a live call.
 *
 * Rules for changing this file:
 *   1. Adding a member is safe.
 *   2. Renaming or removing one is a breaking change; update the Dart binding
 *      in the same commit or the drift test fails.
 *   3. Everything returns a plain value or a Promise. No callbacks that
 *      outlive a call, except where a handle is returned to stop them.
 */

/** Bumped whenever the shape below changes incompatibly. */
export const BRIDGE_VERSION = 1;

// ─── screen share ──────────────────────────────────────────────────────────

/**
 * Which surface to offer first in the browser's picker. The browser always
 * lets the user choose something else; this only sets the default tab.
 */
export type DisplaySurface = 'monitor' | 'window' | 'browser';

export interface ScreenShareOptions {
  /** Preferred surface. Omitted means "let the browser decide". */
  preferred?: DisplaySurface;
  /** Capture system/tab audio alongside the video, where supported. */
  withAudio?: boolean;
  /** Show the "share this tab instead" hint Chrome offers. */
  selfBrowserSurface?: 'include' | 'exclude';
}

export interface ScreenShareApi {
  /** False on browsers with no getDisplayMedia, e.g. iOS Safari. */
  isSupported(): boolean;
  /**
   * Opens the browser's picker. Resolves null when the user cancels --
   * cancelling is a normal outcome, not an error, and must not surface as one.
   */
  pick(options?: ScreenShareOptions): Promise<MediaStream | null>;
}

// ─── connection quality ────────────────────────────────────────────────────

export type QualityLabel = 'excellent' | 'good' | 'poor' | 'unknown';

/**
 * A quality reading, normalised so Dart never has to walk an RTCStatsReport.
 * Mirrors the fields `services/chat`'s `call:media-state` accepts.
 */
export interface QualitySample {
  label: QualityLabel;
  /** Round-trip time in ms; -1 when the browser did not report it. */
  rttMs: number;
  /** 0..100. -1 when unknown. */
  packetLossPercent: number;
  /** Inbound bitrate in kbps since the previous sample. -1 when unknown. */
  inboundKbps: number;
  /** Outbound bitrate in kbps since the previous sample. -1 when unknown. */
  outboundKbps: number;
  at: number;
}

/** Anything with getStats(). Narrowed so tests need no real peer connection. */
export interface StatsSource {
  getStats(): Promise<RTCStatsReport>;
}

export interface QualityPollerHandle {
  stop(): void;
  /** Read the most recent sample without waiting for the next tick. */
  latest(): QualitySample | null;
}

export interface StatsApi {
  /**
   * Poll `source` every `intervalMs` and invoke `onSample`. The callback is
   * only invoked when the label CHANGES or every fifth sample, so a Dart
   * setState is not driven at 1 Hz for a connection that is not moving.
   */
  poll(source: StatsSource, intervalMs: number, onSample: (sample: QualitySample) => void): QualityPollerHandle;

  /** One-shot read, for a preflight probe. */
  sample(source: StatsSource): Promise<QualitySample>;
}

// ─── autoplay ──────────────────────────────────────────────────────────────

export interface AutoplayApi {
  /**
   * True when the browser will refuse to start audio without a gesture.
   * Checked before showing a "tap to hear" affordance.
   */
  isBlocked(): boolean;
  /**
   * Attempt to unlock audio. Call from inside a user-gesture handler.
   * Resolves true when audio can now play.
   */
  unlock(): Promise<boolean>;
}

// ─── live streams (HLS) ────────────────────────────────────────────────────

/** Which implementation is playing. Reported so a UI can explain itself. */
export type HlsEngine = 'native' | 'hlsjs';

export interface HlsAttachment {
  /** Stop playback and release the decoder. Idempotent. */
  destroy(): void;
  readonly engine: HlsEngine;
}

export interface HlsFailure {
  fatal: true;
  /** Machine-readable, from hls.js where it has one. */
  code: string;
  message: string;
}

export interface HlsAttachOptions {
  /** Called on a fatal error, after recovery has been attempted and failed. */
  onFatal?: (failure: HlsFailure) => void;
  /** Called once media is flowing, so a spinner can be dismissed. */
  onPlaying?: () => void;
}

export interface HlsApi {
  /**
   * Whether this browser can play a live stream. Answered WITHOUT downloading
   * the player, so a UI can say "not supported" without a 400 KB round trip.
   */
  isSupported(): boolean;
  /**
   * Point [video] at an .m3u8 and start it. Loads the player bundle on first
   * use; a visitor who never opens a channel never downloads it.
   */
  attach(video: HTMLVideoElement, url: string, options?: HlsAttachOptions): Promise<HlsAttachment>;
}

// ─── casting ───────────────────────────────────────────────────────────

export interface RemoteApi {
  /**
   * Watch for a receiver appearing or disappearing on the network.
   *
   * Calls back immediately with the current state, and `false` once in a
   * browser that cannot cast at all -- so a caller that shows its control
   * only while this says `true` is correct everywhere, and no cast button
   * ever opens a picker that finds nothing.
   *
   * Returns a function that stops the watch. Safe to call twice.
   */
  watchAvailability(element: HTMLMediaElement, onChange: (available: boolean) => void): () => void;
  /**
   * Open the device picker. Must be called from a user gesture. Resolves
   * false only when the browser has no picker to open; a listener who
   * dismisses it is not a failure.
   */
  prompt(element: HTMLMediaElement): Promise<boolean>;
}

// ─── the bridge ────────────────────────────────────────────────────────────

export interface RafeekhMediaBridge {
  readonly version: number;
  readonly screenShare: ScreenShareApi;
  readonly stats: StatsApi;
  readonly autoplay: AutoplayApi;
  readonly hls: HlsApi;
  readonly remote: RemoteApi;
}

declare global {
  interface Window {
    RafeekhMedia?: RafeekhMediaBridge;
  }
}
